<template>
  <div
    id="app"
    data-app
    class="h-100"
    :class="[skinClasses, altBackground ? 'nodiplay' : ''] "
  >
    <impersonation-banner />
    <component :is="layout">
      <router-view />
    </component>

    <!-- modal login-->
    <b-modal
      id="modal-connect"
      ref="modal-connect"
      full-screen
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      append-to="#app"
      centered
      title="ارتباط با سرور"
      :click-to-close="false"
      :ok-disabled="true"
      @close="perventclose"
    >
      <b-spinner
        variant="primary"
        type="grow"
        label="Spinning"
      />
      اتصال با سرور برقرار نشد
      <template #modal-header="" />
      <template #modal-footer="">
        <b-button
          v-if="Platform === 'android'"
          size="sm"
          variant="gradient-danger"
          @click="modalClose()"
        >
          خروج
        </b-button>
        <b-button
          size="sm"
          variant="gradient-primary"
          @click="modalReconnect()"
        >
          تلاش دوباره
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import {
  BCard, BCardBody, BButton, BFormGroup, BFormInput, BForm, BInputGroup, BSpinner,
} from 'bootstrap-vue'

import { useWindowSize, useCssVar } from '@vueuse/core'

import Vue from 'vue'
import store from '@/store'
import { getUserData, isUserLoggedIn } from '@/auth/utils'
import Config from '../config'
import ImpersonationBanner from '@/components/ImpersonationBanner.vue'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    BSpinner,
    BFormGroup,
    BCard,
    BCardBody,
    BButton,
    BFormInput,
    BForm,
    BInputGroup,
    ImpersonationBanner,
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  data() {
    return {
      isUserLoggedIn: isUserLoggedIn(),
      timerCount: 20,
      installBtn: 'none',
      installer: undefined,
      Platform: Vue.prototype.$productionPlatform,
    }
  },
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    altBackground() {
      const path = this.$route.path.split('/')
      return path[path.length - 1].toLowerCase() === ''
    },
  },

  watch: {
    timerCount: {
      handler(value) {
        if (this.isUserLoggedIn !== isUserLoggedIn()) {
          this.timerCount = 8
        }
        this.isUserLoggedIn = isUserLoggedIn()
        if (value >= 0) {
          setTimeout(() => {
            // eslint-disable-next-line no-plusplus
            this.timerCount--
          }, 1000)
        } else {
          this.checkConnect()
          this.timerCount = 20
        }
      },
      immediate: true, // This ensures the watcher is triggered upon creation
    },

  },

  mounted() {
    // اعلام به سیستم گزارش خطا که Vue به درستی لود و mount شده
    if (window.__APP_LIFECYCLE) {
      window.__APP_LIFECYCLE.vueReady = true
      window.__APP_LIFECYCLE.lastActivity = Date.now()
      console.log('App lifecycle: Vue app mounted successfully')
    }

    //
    const $this = this
    // eslint-disable-next-line valid-typeof
    /*
    if(typeof this.sockets !== undefined) {
      this.sockets.subscribe('connect', () => {
        // console.log('socket connect')
      //  Vue.prototype.$socketConnected = true
          $this.$refs['modal-connect'].hide()
      })
      this.sockets.subscribe('disconnect', () => {
        // console.log('socket disconnect')
      //  Vue.prototype.$socketConnected = false
          $this.$refs['modal-connect'].show()
      })
    }
    */
    // Service worker is now handled by registerServiceWorker.js in main.js
  },
  created() {
    let installPrompt

    window.addEventListener('beforeinstallprompt', e => {
      e.preventDefault()
      installPrompt = e
      this.installBtn = 'block'
    })

    this.installer = () => {
      this.installBtn = 'none'
      installPrompt.prompt()
      installPrompt.userChoice.then(result => {
        if (result.outcome === 'accepted') {
          // console.log('Install accepted!')
        } else {
          // console.log('Install denied!')
        }
      })
    }
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')

    if (localStorage.getItem('gpsServers')) {
      Vue.prototype.$gpsServers = JSON.parse(localStorage.getItem('gpsServers'))
    } else {
      Vue.prototype.$gpsServers = Config.gpsServers
    }
    if (localStorage.getItem('apiServers')) {
      Vue.prototype.$apiServers = JSON.parse(localStorage.getItem('apiServers'))
    } else {
      Vue.prototype.$apiServers = Config.apiServers
    }

    // IP-API disabled - using default config
    // fetch('http://ip-api.com/json/?fields=61439')
    //     .then(response => response.json())
    //     .then(data => {
    //       if (data.countryCode === 'IR') {
    //         Vue.prototype.$gpsServers = Config.gpsServers
    //         Vue.prototype.$apiServers = Config.apiServers
    //         localStorage.setItem('gpsServers', JSON.stringify(Config.gpsServers))
    //         localStorage.setItem('apiServers', JSON.stringify(Config.apiServers))
    //       } else {
    //         Vue.prototype.$gpsServers = Config.gpsServersGL
    //         Vue.prototype.$apiServers = Config.apiServersGL
    //         localStorage.setItem('gpsServers', JSON.stringify(Config.gpsServersGL))
    //         localStorage.setItem('apiServers', JSON.stringify(Config.apiServersGL))
    //       }
    //       this.$forceUpdate()
    //     })
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })
    return {
      skinClasses,
    }
  },
  methods: {
    checkConnect() {
      if (Vue.prototype.$socket) {
        if (Vue.prototype.$socket.disconnected && isUserLoggedIn()) {
          //    if (typeof this.$refs['modal-connect'] !== 'undefined') this.$refs['modal-connect'].show()
        }
        if (Vue.prototype.$socket.connected && isUserLoggedIn()) {
          //   if (typeof this.$refs['modal-connect'] !== 'undefined') this.$refs['modal-connect'].hide()
        }
      }
    },
    perventclose(bvModalEvt) {
      bvModalEvt.preventDefault()
    },
    modalClose() {
      exitFromApp()
    },
    modalReconnect() {
      // this.$router.go(this.$router.currentRoute)
      // this.$router.push('/')
      window.location.reload(true)
    },
    getDeviceInfo(imei) {
      for (let i = 0; i <= Vue.prototype.$devices.length - 1; i += 1) {
        if (Vue.prototype.$devices[i].imei === imei) {
          Vue.prototype.$devices[i].$index = i
          return Vue.prototype.$devices[i]
        }
      }
      return null
    },
  },
  /*
  sockets: {
    devicesOnlineTime(imeis) {
      for (const imei in imeis) {
        const index = Vue.prototype.$functions.getDeviceIndex(imei)
        if (Vue.prototype.$devices[index] !== undefined) {
          const diff = this.$functions.time() - imeis[imei]
          if (diff > 2 * 60 * 60) { // 2 ساعت - برای آفلاین واقعی
            Vue.prototype.$devices[index].onlineStatus = 'offlineSymbol'
          } else {
            Vue.prototype.$devices[index].onlineStatus = 'onlineSymbol'
          }
          if ('serverInfo' in Vue.prototype.$devices[index]) {
            Vue.prototype.$devices[index].serverInfo.servertime = imeis[imei]
          }
        }
      }
      this.$root.$emit('forceUpdate', { event: 'socket.devicesOnlineTime', data: imeis })
      this.$forceUpdate()
    },
    deviceStatus(data) {
      // terminalInfo.gpsTracking
      if ('imei' in data) {
        const index = Vue.prototype.$functions.getDeviceIndex(data.imei)
        if (index) {
          Vue.prototype.$devices[index].serverStatus = data
          this.$root.$emit('forceUpdate', { event: 'socket.deviceStatus', data })
          this.$forceUpdate()
        }
      }
    },
    devicePosition(data) {
      // برای غیر موبایل همیشه true هست و برای حالت موبایل
      // بررسی می شود که در صورت وجود در لیست نمایش دهد
      const index = Vue.prototype.$functions.getDeviceIndex(data.imei)
      if (index) {
        Vue.prototype.$devices[index].speed = data.speed
        Vue.prototype.$devices[index].satCnt = data.satCnt
        Vue.prototype.$devices[index].satCntActive = data.satCntActive
        this.$root.$emit('forceUpdate', { event: 'socket.devicePosition', data })
        this.$forceUpdate()
      }
    },
  }, */
}
</script>
